import type { CSSProperties } from "react";
import { Link, useLocation, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { NAV_ROUTES, matchRoute } from "@/components/shell/navConfig";
import { recallListSearch } from "@/lib/listState";
import { previousScreen } from "@/lib/screenTrail";

// Top left on a card: back to where you came from. Opened from another screen — a location
// from the order you were working on, a trip from the planning board — it goes back to that
// screen as you left it and says so ("← ORD-000123"). Opened from its own list, from the menu
// or in a new tab, it is the link to the list it always was, with the list as you left it
// (lib/listState). See lib/screenTrail for how the way back is known.

const LINK_STYLE: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "var(--space-1)",
  color: "var(--text-secondary)",
};

function Arrow() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  );
}

export function BackLink({ list, listLabel, style }: { list: string; listLabel: string; style?: CSSProperties }) {
  const { t } = useTranslation();
  const router = useRouter();
  const location = useLocation();
  const back = previousScreen(location);
  const linkStyle = { ...LINK_STYLE, ...style };

  if (back && back.pathname !== list) {
    const route = matchRoute(back.pathname, NAV_ROUTES);
    const label = back.label ?? (route ? t(route.labelKey as never) : t("common.back"));
    return (
      <a
        href={back.href}
        className="t-caption"
        style={linkStyle}
        aria-label={t("nav.backTo", { module: label })}
        onClick={(e) => {
          // A click with a modifier opens the screen in a new tab, as any link does.
          if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
          e.preventDefault();
          // Back through the history, not forward to a copy: the next "terug" there goes
          // on to where that screen was opened from.
          router.history.go(-back.steps);
        }}
      >
        <Arrow />
        {label}
      </a>
    );
  }

  return (
    <Link
      to={list}
      search={() => recallListSearch(list)}
      className="t-caption"
      style={linkStyle}
      aria-label={t("nav.backTo", { module: listLabel })}
    >
      <Arrow />
      {listLabel}
    </Link>
  );
}
